import type { Cup, DailyStat, DrinkEntry, HydrationGoal, WeightEntry } from "../types/models";
import { createIdempotencyKey } from "../lib/idempotency";
import { getEffectiveTimezone, toIsoNow } from "../lib/timezone";
import { apiData, apiPath, type Schema } from "./api";

const MAX_PAGE_SIZE = 200;

export async function listCups(): Promise<Cup[]> {
  const data = await apiData<Schema<"CupResponseListEnvelope">>("/cups", { query: { limit: 50, offset: 0 } });
  return data.filter(hasId);
}

export async function createCup(body: Schema<"CreateCupRequest">): Promise<Cup | null> {
  const data = await apiData<Schema<"IDResponseEnvelope">, Schema<"CreateCupRequest">>("/cups", {
    method: "POST",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  return data.id ? findCup(data.id) : null;
}

export async function updateCup(id: string, body: Schema<"UpdateCupRequest">): Promise<Cup | null> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"UpdateCupRequest">>(apiPath(`/cups/${id}`), {
    method: "PATCH",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  return findCup(id);
}

export async function deleteCup(id: string): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>(apiPath(`/cups/${id}`), { method: "DELETE" });
}

export async function listDrinkEntries(limit = 100, offset = 0): Promise<DrinkEntry[]> {
  const data = await apiData<Schema<"DrinkEntryResponseListEnvelope">>("/drink-entries", { query: { limit: Math.min(limit, MAX_PAGE_SIZE), offset } });
  return data.filter(hasId);
}

export async function listAllDrinkEntries(): Promise<DrinkEntry[]> {
  return listAllPages((limit, offset) => listDrinkEntries(limit, offset));
}

export async function recordDrinkFromCup(input: {
  cup: Cup;
  timezoneMode: "detected" | "fixed";
  fixedTimezone: string;
  entryTime?: string;
  idempotencyKey?: string;
}): Promise<DrinkEntry | null> {
  const timezone = getEffectiveTimezone(input.timezoneMode, input.fixedTimezone);
  const body: Schema<"CreateDrinkEntryRequest"> = {
    cup_id: input.cup.id,
    amount_ml: input.cup.volume_ml ?? 0,
    entry_time: input.entryTime ?? toIsoNow(),
    timezone,
    note: ""
  };
  const created = await createDrinkEntry(body, input.idempotencyKey);
  return created ? withCupSnapshotFallback(created, input.cup) : null;
}

export async function createDrinkEntry(body: Schema<"CreateDrinkEntryRequest">, idempotencyKey = createIdempotencyKey()): Promise<DrinkEntry | null> {
  const data = await apiData<Schema<"IDResponseEnvelope">, Schema<"CreateDrinkEntryRequest">>("/drink-entries", {
    method: "POST",
    body,
    idempotencyKey
  });
  if (!data.id) {
    return null;
  }
  return (await findDrinkEntry(data.id)) ?? requestBackedDrinkEntry(data.id, body);
}

export async function updateDrinkEntry(id: string, body: Schema<"UpdateDrinkEntryRequest">): Promise<DrinkEntry | null> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"UpdateDrinkEntryRequest">>(apiPath(`/drink-entries/${id}`), {
    method: "PATCH",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  return findDrinkEntry(id);
}

export async function deleteDrinkEntry(id: string): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>(apiPath(`/drink-entries/${id}`), { method: "DELETE" });
}

export async function listWeightEntries(limit = 100, offset = 0): Promise<WeightEntry[]> {
  const data = await apiData<Schema<"WeightEntryResponseListEnvelope">>("/weight-entries", { query: { limit: Math.min(limit, MAX_PAGE_SIZE), offset } });
  return data.filter(hasId);
}

export async function listAllWeightEntries(): Promise<WeightEntry[]> {
  return listAllPages((limit, offset) => listWeightEntries(limit, offset));
}

export async function createWeightEntry(body: Schema<"CreateWeightEntryRequest">): Promise<WeightEntry | null> {
  const data = await apiData<Schema<"IDResponseEnvelope">, Schema<"CreateWeightEntryRequest">>("/weight-entries", {
    method: "POST",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  return data.id ? findWeightEntry(data.id) : null;
}

export async function updateWeightEntry(id: string, body: Schema<"UpdateWeightEntryRequest">): Promise<WeightEntry | null> {
  await apiData<Schema<"BooleanResponseEnvelope">, Schema<"UpdateWeightEntryRequest">>(apiPath(`/weight-entries/${id}`), {
    method: "PATCH",
    body,
    idempotencyKey: createIdempotencyKey()
  });
  return findWeightEntry(id);
}

export async function deleteWeightEntry(id: string): Promise<void> {
  await apiData<Schema<"BooleanResponseEnvelope">>(apiPath(`/weight-entries/${id}`), { method: "DELETE" });
}

export async function listDailyStats(limit = 60, offset = 0): Promise<DailyStat[]> {
  return apiData<Schema<"DailySummaryResponseListEnvelope">>("/stats/daily", { query: { limit: Math.min(limit, MAX_PAGE_SIZE), offset } });
}

export async function listAllDailyStats(): Promise<DailyStat[]> {
  return listAllPages((limit, offset) => listDailyStats(limit, offset));
}

export async function getHydrationGoals(): Promise<HydrationGoal[]> {
  const data = await apiData<Schema<"HydrationGoalResponseListEnvelope">>("/hydration/goals", { query: { limit: 50, offset: 0 } });
  return data.filter(hasId);
}

export async function setHydrationGoal(goalMl: number): Promise<HydrationGoal | null> {
  const data = await apiData<Schema<"HydrationGoalResponseEnvelope">, Schema<"SetHydrationGoalRequest">>("/hydration/goals", {
    method: "POST",
    body: { goal_ml: goalMl },
    idempotencyKey: createIdempotencyKey()
  });
  return data.id ? (data as HydrationGoal) : null;
}

async function findCup(id: string): Promise<Cup | null> {
  return (await listCups()).find((cup) => cup.id === id) ?? null;
}

async function findDrinkEntry(id: string): Promise<DrinkEntry | null> {
  return (await listAllDrinkEntries()).find((entry) => entry.id === id) ?? null;
}

async function findWeightEntry(id: string): Promise<WeightEntry | null> {
  return (await listAllWeightEntries()).find((entry) => entry.id === id) ?? null;
}

function requestBackedDrinkEntry(id: string, body: Schema<"CreateDrinkEntryRequest">): DrinkEntry {
  return {
    id,
    amount_ml: body.amount_ml,
    cup_id: body.cup_id,
    entry_time: body.entry_time,
    note: body.note ?? undefined,
    timezone: body.timezone
  };
}

function withCupSnapshotFallback(entry: DrinkEntry, cup: Cup): DrinkEntry {
  return {
    ...entry,
    cup_id: entry.cup_id ?? cup.id,
    cup_icon_snapshot: entry.cup_icon_snapshot ?? cup.icon ?? "cup",
    cup_volume_snapshot_ml: entry.cup_volume_snapshot_ml ?? cup.volume_ml ?? null
  };
}

function hasId<T extends { id?: string }>(item: T): item is T & { id: string } {
  return Boolean(item.id);
}

async function listAllPages<T>(loadPage: (limit: number, offset: number) => Promise<T[]>): Promise<T[]> {
  const items: T[] = [];
  let offset = 0;
  for (;;) {
    const page = await loadPage(MAX_PAGE_SIZE, offset);
    items.push(...page);
    if (page.length < MAX_PAGE_SIZE) {
      return items;
    }
    offset += MAX_PAGE_SIZE;
  }
}
